// Ctrl+K / Cmd+K command palette: jump to a region/country/organization, or
// run a layer/mode command, without touching the sidebar. Arrow keys move
// the highlight, Enter runs it; Escape/Ctrl+K-again close it (handled by
// the caller so there's one source of truth for the open state).
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icons'
import '../styles/CommandPalette.css'

const buildStaticCommands = (actions) => [
  { id: 'vue-normale', label: 'Vue : Normale', icon: 'view', run: actions.setVueNormale },
  { id: 'vue-satellite', label: 'Vue : Satellite', icon: 'view', run: actions.setVueSatellite },
  { id: 'vue-topo', label: 'Vue : Topographie', icon: 'view', run: actions.setVueTopo },
  { id: 'viz-neutral', label: 'Visualisation : Carte neutre', icon: 'layers', run: () => actions.onToggleLayer(null, false) },
  { id: 'viz-countries', label: 'Visualisation : Contrôle territorial', icon: 'layers', run: () => actions.onToggleLayer('countries') },
  { id: 'viz-played', label: 'Visualisation : Pays joués', icon: 'layers', run: () => actions.onToggleLayer('playedCountries') },
  { id: 'viz-population', label: 'Visualisation : Densité de population', icon: 'layers', run: () => actions.onToggleLayer('population') },
  { id: 'viz-climate', label: 'Visualisation : Zones climatiques', icon: 'layers', run: () => actions.onToggleLayer('climate') },
  { id: 'viz-weather', label: 'Visualisation : Météo en direct', icon: 'layers', run: () => actions.onToggleLayer('weather') },
  { id: 'viz-hemisphere', label: 'Visualisation : Hémisphères', icon: 'layers', run: () => actions.onToggleLayer('hemisphere') },
  { id: 'border-none', label: 'Frontières : Aucune', icon: 'compass', run: () => actions.onBorderModeChange('none') },
  { id: 'border-region', label: 'Frontières : Régions', icon: 'compass', run: () => actions.onBorderModeChange('region') },
  { id: 'border-country', label: 'Frontières : Pays', icon: 'compass', run: () => actions.onBorderModeChange('country') },
  { id: 'ruler', label: 'Basculer le mode Règle', icon: 'crosshair', run: actions.onToggleRulerMode },
  { id: 'plan', label: 'Basculer le Mode Planification', icon: 'palette', run: actions.onTogglePlanMode },
  { id: 'construction', label: 'Basculer le Mode Construction', icon: 'building', run: actions.onToggleConstructionMode },
  { id: 'globe', label: 'Basculer Vue Globe / Plane', icon: 'globe', run: actions.onToggleGlobeView },
]

// Execution history: last commands/jumps run from the palette, shown as a
// "Récents" block when the query is empty. Stored as re-runnable references
// (command id / region id / org id), resolved back to live data on render.
const HISTORY_KEY = 'onlinemap-cmdk-history'
const HISTORY_MAX = 6

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    return Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

const pushHistory = (entry) => {
  const next = [entry, ...loadHistory().filter((h) => !(h.kind === entry.kind && h.id === entry.id))]
    .slice(0, HISTORY_MAX)
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)) } catch { /* full/blocked */ }
}

const CommandPalette = ({
  open, onClose, regionsData, organizationsCatalog,
  onCenterMap, onSelectOrg, ...actions
}) => {
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [history, setHistory] = useState([])
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setHighlight(0)
      setHistory(loadHistory())
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [open])

  const staticCommands = useMemo(() => buildStaticCommands(actions), [actions])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    const out = []

    // Empty query: recents first, resolved back against live data (a region
    // or org that no longer exists just drops out).
    if (!q && history.length) {
      history.forEach((h) => {
        if (h.kind === 'command') {
          const c = staticCommands.find((x) => x.id === h.id)
          if (c) out.push({ kind: 'command', key: `recent-${h.id}`, icon: c.icon, label: c.label, recent: true, historyEntry: h, run: c.run })
        } else if (h.kind === 'region' && regionsData?.features) {
          const f = regionsData.features.find((x) => x.properties.region_id === h.id)
          if (f) {
            const p = f.properties
            out.push({
              kind: 'region', key: `recent-region-${h.id}`, icon: 'crosshair', recent: true, historyEntry: h,
              label: `${p.name}${p.country_name ? ` — ${p.country_name}` : ''}`,
              run: () => onCenterMap(p),
            })
          }
        } else if (h.kind === 'org' && organizationsCatalog?.[h.id]) {
          const org = organizationsCatalog[h.id]
          out.push({
            kind: 'org', key: `recent-org-${h.id}`, icon: 'shield', recent: true, historyEntry: h,
            label: `Organisation : ${org.name}`,
            run: () => onSelectOrg(org.org_id),
          })
        }
      })
    }

    staticCommands.forEach((c) => {
      if (!q || c.label.toLowerCase().includes(q)) {
        out.push({ kind: 'command', key: c.id, icon: c.icon, label: c.label, historyEntry: { kind: 'command', id: c.id }, run: c.run })
      }
    })

    if (q && regionsData?.features) {
      let count = 0
      for (const f of regionsData.features) {
        if (count >= 8) break
        const p = f.properties
        const hay = `${p.name || ''} ${p.country_name || ''} ${p.region_id || ''}`.toLowerCase()
        if (hay.includes(q)) {
          out.push({
            kind: 'region', key: `region-${p.region_id}`, icon: 'crosshair',
            label: `${p.name}${p.country_name ? ` — ${p.country_name}` : ''}`,
            historyEntry: { kind: 'region', id: p.region_id },
            run: () => onCenterMap(p),
          })
          count++
        }
      }
    }

    if (q && organizationsCatalog) {
      Object.values(organizationsCatalog).forEach((org) => {
        if (org.name?.toLowerCase().includes(q)) {
          out.push({
            kind: 'org', key: `org-${org.org_id}`, icon: 'shield',
            label: `Organisation : ${org.name}`,
            historyEntry: { kind: 'org', id: org.org_id },
            run: () => onSelectOrg(org.org_id),
          })
        }
      })
    }

    return out.slice(0, 24)
  }, [query, staticCommands, regionsData, organizationsCatalog, onCenterMap, onSelectOrg, history])

  useEffect(() => setHighlight(0), [query])

  if (!open) return null

  const runResult = (r) => {
    if (!r) return
    if (r.historyEntry) pushHistory(r.historyEntry)
    r.run()
    onClose()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      runResult(results[highlight])
    }
  }

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <div className="command-palette-input-row">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Région, pays, organisation, calque…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="command-palette-results">
          {results.length === 0 && (
            <div className="command-palette-empty">Aucun résultat</div>
          )}
          {results.map((r, i) => (
            <Fragment key={r.key}>
              {i === 0 && r.recent && (
                <div className="command-palette-group">Récents</div>
              )}
              {i > 0 && results[i - 1].recent && !r.recent && (
                <div className="command-palette-group">Commandes</div>
              )}
              <button
                className={`command-palette-item ${i === highlight ? 'highlight' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => runResult(r)}
              >
                <Icon name={r.recent ? 'undo' : r.icon} size={15} />
                <span>{r.label}</span>
              </button>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
